import { differenceInCalendarDays, parseISO } from 'date-fns';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompanionCard, usePager } from '@/components/companion/CompanionCard';
import { formatTime, formatWeekday } from '@/i18n/intl';
import { msg, useMsg, type Msg } from '@/i18n/msg';
import { countsForCalendarSuggestion } from '@/lib/calendar-display';
import type { MedicalAppointment, Task } from '@/types/api';

/**
 * The note, its button and the prompt it sends are message descriptors
 * (#2109 R3-B3), resolved by the card at render and by the page on press —
 * the prompt in the UI language (R3-B2).
 */
export interface CalendarPattern {
  id: string;
  body: Msg;
  ctaLabel: Msg;
  onCta: () => void;
}

interface BuildArgs {
  appointments: MedicalAppointment[];
  tasks: Task[];
  onTalkToStevens: (prompt: Msg) => void;
  onCreateTask: () => void;
  companionName: string;
  /** The formatting locale for the weekday and time params; the page passes the UI language and rebuilds on a switch. */
  locale?: string;
}

// Note: rules favor short, calm copy that calls out a SPECIFIC pattern in
// the data. Each rule produces 0 or 1 suggestion — duplicate-free.
export function buildCalendarPatterns({
  appointments,
  tasks,
  onTalkToStevens,
  onCreateTask,
  companionName,
  locale,
}: BuildArgs): CalendarPattern[] {
  const out: CalendarPattern[] = [];
  const now = new Date();
  // #2433 (integrator ruling): the same rule as the calendar grid's rest-day
  // suggestion. A cancelled or missed visit raises no remark; an attended one
  // still does, so an attended chemo today still raises 'chemo-followday'.
  // The scan/lab rule uses the same basis.
  const live = appointments.filter(countsForCalendarSuggestion);

  // Rule 1: chemo Wed → low-energy Thu without a claimed helper
  const upcomingChemo = live.find((a) => {
    if (a.appointment_type !== 'chemotherapy') return false;
    const d = differenceInCalendarDays(parseISO(a.date_time), now);
    return d >= 0 && d <= 7;
  });
  if (upcomingChemo) {
    const chemoDay = parseISO(upcomingChemo.date_time);
    const day = formatWeekday(chemoDay, { width: 'long', locale });
    const nextDay = formatWeekday(new Date(chemoDay.getTime() + 86400000), { width: 'long', locale });
    out.push({
      id: 'chemo-followday',
      body: msg('calendar:callout.chemoFollowDay', { day, nextDay }),
      ctaLabel: msg('calendar:callout.chemoCta', { nextDay }),
      onCta: onCreateTask,
    });
  }

  // Rule 2: MRI / scan with no claimed helper sit-with task that day
  const upcomingScan = live.find((a) => {
    if (a.appointment_type !== 'scan' && a.appointment_type !== 'lab') return false;
    const d = differenceInCalendarDays(parseISO(a.date_time), now);
    return d >= 0 && d <= 7;
  });
  const scanAt = upcomingScan ? parseISO(upcomingScan.date_time) : null;
  if (upcomingScan && scanAt && !Number.isNaN(scanAt.getTime())) {
    const day = formatWeekday(scanAt, { width: 'long', locale });
    const time = formatTime(scanAt, { locale });
    const sitTaskExists = tasks.some(
      (t) =>
        t.date_time &&
        t.category === 'hospital_stay' &&
        differenceInCalendarDays(parseISO(t.date_time), parseISO(upcomingScan.date_time)) === 0,
    );
    if (!sitTaskExists) {
      out.push({
        id: 'scan-sitwith',
        // One key: no language inherits the English possessive built in code.
        body: msg('calendar:callout.scanSitWith', { day, title: upcomingScan.title, time }),
        ctaLabel: msg('calendar:callout.scanCta'),
        onCta: onCreateTask,
      });
    }
  }

  // Rule 3: Multiple urgent tasks the same week
  const urgent = tasks.filter(
    (t) =>
      t.status === 'open' &&
      (t.urgency === 'critical' || t.urgency === 'high') &&
      t.date_time &&
      differenceInCalendarDays(parseISO(t.date_time), now) >= 0 &&
      differenceInCalendarDays(parseISO(t.date_time), now) <= 7,
  );
  if (urgent.length >= 2) {
    const count = urgent.length;
    out.push({
      id: 'urgent-cluster',
      body: msg('calendar:callout.urgentCluster', { count }),
      ctaLabel: msg('calendar:callout.talkTo', { companion: companionName }),
      onCta: () => onTalkToStevens(msg('calendar:callout.urgentPrompt', { count })),
    });
  }

  return out;
}

interface CalendarStevensCalloutProps {
  patterns: CalendarPattern[];
}

export function CalendarStevensCallout({ patterns }: CalendarStevensCalloutProps) {
  const resolve = useMsg();
  const { index, pager } = usePager(patterns.length);
  const current = patterns.length ? patterns[index] : null;
  if (!current) return null;

  // One companion shape platform-wide (declutter 2026-09-10, C5): the shared
  // CompanionCard owns the indigo shell and the sentence-case label. The
  // calendar's own status-ok tint and mono "Noticed a pattern" eyebrow go —
  // green is the status-ok hue, and a remark is not a status.
  return (
    <CompanionCard layout="card" body={resolve(current.body)} pager={pager} data-testid="calendar-companion-callout">
      <Button size="sm" variant="brand" className="h-8 text-caption" onClick={current.onCta}>
        <Plus className="h-3 w-3 mr-1" />
        {resolve(current.ctaLabel)}
      </Button>
    </CompanionCard>
  );
}
